/**
 * No-op compatibility shim for the legacy `$dialogueManager` API.
 *
 * The old dialogue plugin subscribed to events via an AWS API Gateway WebSocket
 * that was decommissioned in the 2026 realtime migration. The new realtime
 * transport is the `$realtimeBus` in `src/plugins/realtimeBus.js` (fed by
 * `src/plugins/realtime.js`).
 *
 * ~19 components in the codebase still reference `this.$dialogueManager` via
 * the old subscribe/onIncomingMessage pattern. Migrating all of them to the
 * event bus was out of scope for that migration, and those code paths
 * had already stopped receiving events in production (the Lambda was dead), so
 * this shim preserves the current "silently gets nothing" behaviour rather than
 * throwing `TypeError: Cannot read property 'subscribe' of undefined`.
 *
 * Each method is a no-op that returns the shape the callers expect:
 *   .start()                    -> void
 *   .stop()                     -> void
 *   .destroy()                  -> void
 *   .personalize(user)          -> void
 *   .whenReady(cb)              -> never fires (callback is held but never called)
 *   .subscribe(topic)           -> returns a no-op dialogue handle
 *   .unsubscribe(dialogue)      -> void
 *   .send(topic, msg, options)  -> void
 *   .getDialog(topic)           -> null
 *
 * New code must NOT use this shim — use `this.$realtimeBus.$on(eventName, handler)`
 * in `mounted()` / `$off` in `beforeDestroy()` instead.
 */

const noopDialogue = {
    mute() {},
    unmute() {},
    onIncomingMessage() {},
    topic() { return '' },
    id() { return '' },
}

const noopManager = {
    start() {},
    stop() {},
    destroy() {},
    personalize() {},
    whenReady() {},
    subscribe() { return noopDialogue },
    unsubscribe() {},
    send() {},
    getDialog() { return null },
}

export default {
    install(app) {
        app.config.globalProperties.$dialogueManager = noopManager
    },
}
